import { moderateImage } from '../../platform/dataSources/moderation';
import {
  DEFAULT_MESSAGE_HISTORY_CONTAINER_WIDTH,
  IMAGE_SKELETON_PLACEHOLDER_MAX_WIDTH_PROPORTION,
  IMAGE_SKELETON_PLACEHOLDER_MAX_HEIGHT_PROPORTION,
} from '../constants';

/* eslint-disable no-param-reassign */

// Fisher-Yates shuffle algorithm
const shuffle = (arr) => {
  let endI = arr.length - 1;

  while (endI) {
    let randI = Math.floor(Math.random() * endI);
    // eslint-disable-next-line no-param-reassign
    [arr[endI], arr[randI]] = [arr[randI], arr[endI]];
    endI -= 1;
  }

  return arr;
};

export const getRandomElements = (list, items = 3) => shuffle([...list]).slice(0, items);

// Delegates to the platform moderation adapter (the transport now lives there).
// Callers migrate to `moderateImage` directly in a later phase, then this shim goes.
export const getContentModeration = (apiOnlineHost, imageFile, uploadContext) => (
  moderateImage({ host: apiOnlineHost, imageFile, context: uploadContext })
);

/**
 * Formats a millisecond timestamp into h:mmAM/PM format.
 * @param {number} timestamp
 * @returns {string}
 */
export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes}${ampm}`;
};

// PROTOTYPE (perf): shared Intl formatter, memoized by calendar day — see
// .claude/context/drawer-layout.md.
const DATE_FORMATTER = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const _dateCache = new Map();

export const formatDate = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const dayKey = date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate();
  const cached = _dateCache.get(dayKey);
  if (cached !== undefined) return cached;
  const formatted = DATE_FORMATTER.format(date);
  _dateCache.set(dayKey, formatted);
  return formatted;
};

export const getImageDimensions = (aspectRatio, containerWidth = DEFAULT_MESSAGE_HISTORY_CONTAINER_WIDTH) => {
  const maxWidth = containerWidth * IMAGE_SKELETON_PLACEHOLDER_MAX_WIDTH_PROPORTION;
  const maxHeight = window.innerHeight * IMAGE_SKELETON_PLACEHOLDER_MAX_HEIGHT_PROPORTION;
  let width = maxWidth;
  let height = width / aspectRatio;
  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspectRatio;
  }
  return { width: Math.round(width), height: Math.round(height) };
};
/**
 * The surface the assistant is running on, as one value.
 *
 * `isConsumerApp` is checked before `channel` because the consumer app reports the
 * mobile channel — reading channel first would report it as mobile web.
 *
 * THE single device-type resolver: the Sprinklr conversation context (MAG4-856) and both
 * GraphQL payloads go through this one function. `queryLayer/variables.js` used to carry a
 * second rule — 'app' only when the channel was ALSO mobile, raw channel otherwise — which
 * disagreed here for a consumer app reporting a desktop channel, and returned `undefined`
 * for an absent channel. That is gone: the bot and the live agent now describe the shopper's
 * surface the same way, and the payload always carries a real value.
 *
 * ⚠ `messages/MessageFeedback.jsx` still builds its own 'Mobile'/'Desktop' (capitalised, and
 * blind to isConsumerApp) for the feedback operation's own schema. Left alone on purpose —
 * that is a different contract with a non-null field, not this one.
 *
 * @param {Object} experienceContext - Fields off ExperienceContext.
 * @param {string} [experienceContext.channel] - 'desktop' or 'mobile'.
 * @param {boolean} [experienceContext.isConsumerApp] - True inside the THD app.
 * @returns {'app'|'mobile'|'desktop'} Defaults to desktop, as `channel` itself does.
 */
export const getDeviceType = ({ channel, isConsumerApp } = {}) => {
  if (isConsumerApp) return 'app';
  if (channel === 'mobile') return 'mobile';
  return 'desktop';
};
